import { useEffect, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { psiTokens } from '../theme/psiTheme';

// Auto-rotating logo carousel. Slides continuously to the right (new logos
// enter from the right edge) by translating a duplicated track and snapping
// back invisibly once the duplicate set scrolls fully into view.
export default function LogoCarousel({ items, intervalMs = 3000 }) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const itemsPerView = isXs ? 1 : isSm ? 2 : 3;

  const [index, setIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const timerRef = useRef(null);
  const pausedRef = useRef(false);

  const trackItems = items.length > itemsPerView ? [...items, ...items.slice(0, itemsPerView)] : items;
  const canLoop = items.length > itemsPerView;

  useEffect(() => {
    setIndex(0);
    setTransitionEnabled(true);
  }, [itemsPerView]);

  useEffect(() => {
    if (!canLoop) return undefined;
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) {
        setIndex((prev) => prev + 1);
      }
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [canLoop, intervalMs]);

  const handleTransitionEnd = () => {
    if (index === items.length) {
      setTransitionEnabled(false);
      setIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTransitionEnabled(true));
      });
    }
  };

  const itemWidthPct = 100 / trackItems.length;
  const trackWidthPct = (trackItems.length / itemsPerView) * 100;

  return (
    <Box
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
      sx={{ overflow: 'hidden', width: '100%' }}
    >
      <Box
        onTransitionEnd={handleTransitionEnd}
        sx={{
          display: 'flex',
          width: `${trackWidthPct}%`,
          transform: `translateX(-${index * itemWidthPct}%)`,
          transition: transitionEnabled ? 'transform 0.7s ease' : 'none',
        }}
      >
        {trackItems.map((item, idx) => (
          <Box
            key={`${item.name}-${idx}`}
            sx={{
              flex: `0 0 ${itemWidthPct}%`,
              boxSizing: 'border-box',
              px: 1,
            }}
          >
            <LogoTile item={item} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function LogoTile({ item }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = item.src && !imageFailed;

  return (
    <Box
      sx={{
        border: `1px solid ${psiTokens.border}`,
        borderRadius: '12px',
        p: 2,
        aspectRatio: '2 / 1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: psiTokens.background.primary,
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={item.src}
          alt={item.name}
          onError={() => setImageFailed(true)}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: psiTokens.text.secondary,
            textAlign: 'center',
          }}
        >
          {item.name}
        </Typography>
      )}
    </Box>
  );
}
